import React, {useEffect, useRef, useState} from 'react';
import {AppState} from 'react-native';
import {activeMeasurement} from '../../../../shared/hardware-measurement.js';
import {hardwareWaitSeconds, type HardwareCommand} from '../../../hardware-types';
import {useLive} from '../../live';
import {useTokens} from '../../theme/tokens';
import {Text} from '../../copy';
import {Button, Card, Segmented, StatePanel} from '../primitives';
import {Icon} from '../Icon';
import {MeasurementExperience} from './MeasurementExperience';
import {measurementDanish, measurementEnglish} from './measurementCopy';
import type {MeasurementKind} from './measurementModel';
import {beginMeasurement, dismissMeasurement, observeMeasurement, measurementSessionFrame, measurementSessionValues, type MeasurementSession} from './measurementSession';

export function MeasurementFlow({scope}:{scope:string}) {
  const {state, session, tracker, locale, actions}=useLive(), {colors,type}=useTokens();
  const hardware=state.snapshot?.hardware?.deviceId===tracker.entity.deviceId?state.snapshot.hardware:undefined;
  const wonlex=hardware?.capabilities.wonlex===true;
  const initial=useRef(hardware&&activeMeasurement(hardware.commands,hardware.reports,Date.now()));
  const [kind,setKind]=useState<MeasurementKind>(initial.current?.type==='BODYTEMP2'?'temperature':'combined');
  const [presentation,setPresentation]=useState<MeasurementSession|null>(()=>initial.current?beginMeasurement(scope,initial.current):null);
  const [now,setNow]=useState(Date.now), [foreground,setForeground]=useState(AppState.currentState==='active');
  const sending=useRef(false), mounted=useRef(true);
  useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
  useEffect(()=>{const sub=AppState.addEventListener('change',value=>{setForeground(value==='active');setNow(Date.now());});return()=>sub.remove();},[]);
  const visible=Boolean(presentation&&!presentation.dismissed);
  const frame=presentation?measurementSessionFrame(presentation,now):null;
  const animating=visible&&frame!==null&&['waiting','measuring','delayed'].includes(frame.phase);
  useEffect(()=>{if(!foreground||(visible&&!animating))return;const timer=setInterval(()=>setNow(Date.now()),animating?150:1000);return()=>clearInterval(timer);},[foreground,animating,visible]);
  const pending=hardware&&activeMeasurement(hardware.commands,hardware.reports,now);
  useEffect(()=>{
    setPresentation(previous=>{
      if(!previous)return null;
      const current=hardware?.commands.find(command=>command.id===previous.command.id);
      // A newer measurement replaces the old presentation; never reuse its values.
      const newer=hardware?.commands.filter(command=>['HRTSTART','BODYTEMP2'].includes(command.type)&&command.id>previous.command.id)
        .sort((a,b)=>b.id-a.id)[0];
      if(previous.command.id>0&&newer&&!previous.dismissed)return beginMeasurement(scope,newer);
      // A completed animation stops its clock; a later oxygen packet still needs
      // the actual snapshot observation time to pass the freshness check.
      return observeMeasurement(previous,scope,current??previous.command,hardware?.reports??[],Date.now());
    });
  },[hardware,scope,now]);
  const displayKind=visible&&presentation?presentation.command.type==='BODYTEMP2'?'temperature':'combined':kind;
  const commandType=displayKind==='temperature'?'BODYTEMP2':'HRTSTART';
  const startDisabled=state.busy||state.snapshot?.device?.access?.canConfigure===false||!state.snapshot?.device?.connected||state.networkUnavailable||!hardware?.capabilities.commands.includes(commandType)
    ||Boolean(pending)||hardwareWaitSeconds(hardware,commandType,now)>0;
  const start=async()=>{
    if(startDisabled||sending.current)return;
    sending.current=true;
    const queuedAt=new Date().toISOString();
    const provisional: HardwareCommand={id:-1,type:commandType,payload:commandType,status:'queued',queuedAt,sentAt:null,repliedAt:null};
    setPresentation(beginMeasurement(scope,provisional));
    let accepted=false;
    try {
      await session.sendHardwareCommand(commandType,{},true,id=>{
        accepted=true;
        if(mounted.current)setPresentation(beginMeasurement(scope,{...provisional,id}));
      });
    } finally {
      sending.current=false;
      if(mounted.current&&!accepted)setPresentation(null);
    }
  };
  const dismiss=()=>{setKind(displayKind);setPresentation(previous=>previous?dismissMeasurement(previous):null);};
  const baseCopy=locale==='da-DK'?measurementDanish:measurementEnglish;
  const invalid=presentation?.report&&presentation.report.quality!=='reported';
  const copy={...baseCopy,
    cancel:locale==='da-DK'?'Luk målevisning':'Close measurement view',
    ...(invalid?{error:locale==='da-DK'?'Utilstrækkelige data':'Insufficient data',
      errorHelp:locale==='da-DK'?'Armbåndet sendte ikke en gyldig måling. Kontrollér, at det sidder rigtigt, og prøv igen.':'The bracelet did not send a valid reading. Check its fit and try again.'}:{})};
  return <>
    <Segmented label="Vælg måling" value={displayKind} onChange={value=>{setKind(value as MeasurementKind);setPresentation(null);}} disabled={animating||sending.current}
      options={[{value:'combined',label:wonlex?(locale==='da-DK'?'Puls og blodtryk':'Pulse and blood pressure'):'Puls, blodtryk og iltmætning'},...(!wonlex||hardware?.capabilities.temperature?[{value:'temperature',label:'Temperatur'}]:[])]}/>
    {visible&&presentation&&frame?<MeasurementExperience sessionKey={`${scope}:${presentation.command.id}:${displayKind}`}
      includeOxygen={!wonlex} kind={displayKind} phase={frame.phase} elapsedMs={frame.elapsedMs} progress={frame.progress}
      values={measurementSessionValues(presentation)} colors={colors} locale={locale} copy={copy}
      renderIcon={(name,size,color)=><Icon name={name} size={size} color={color}/>}
      active={foreground} onCancel={dismiss} onAgain={dismiss} onHistory={()=>actions.openScreen('care-history')}/>
      :<Card><Text style={{color:colors.textMuted,fontSize:type.bodyLarge,lineHeight:type.bodyLarge+7}}>Sørg for, at armbåndet sidder roligt. Resultatet kan mangle, hvis forbindelsen afbrydes eller data er utilstrækkelige.</Text>
        {pending&&<Text style={{color:colors.textMuted,fontSize:type.bodyLarge,lineHeight:type.bodyLarge+7}}>{locale==='da-DK'?'Armbåndet kan stadig måle. Vent, før du starter igen.':'The bracelet may still be measuring. Wait before starting again.'}</Text>}
        <Button label="Start én måling" icon="measure" disabled={Boolean(startDisabled)} onPress={()=>void start()}/></Card>}
    {(!state.snapshot?.device?.connected||state.networkUnavailable)&&<StatePanel state="offline"/>}
    {frame?.phase!=='complete'||!visible?<Button label="Se målehistorik" fontSize={20} icon="history" tone="secondary" onPress={()=>actions.openScreen('care-history')}/>:null}
  </>;
}
